import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

import { convertToArray, getObjectKeys } from "@/helpers/common";
import {
  DISCOVERY_FILTER_OPTIONS,
  NOT_ADDITIONAL_TRACKING_TOKENS,
  NOT_ANALYST_IDS,
  NOT_CLIENT_TRACKING_TOKENS,
  NOT_FUNDED_OFFER_FUNDERS,
  NOT_FUNDERS_WITH_OFFERS,
  NOT_FUNDERS_WITH_PLACEMENTS,
  NOT_FUNDING_ADVISOR_IDS,
  NOT_ORIGIN_PARTNERS,
  NOT_PARTNERS,
  NOT_PRODUCT_TYPE_FUNDED,
  NOT_TASK_ASSIGNEES,
  NOT_TASK_PRIORITY_LEVELS,
  NOT_TASK_STATUSES,
  NOT_UNDERWRITER_IDS,
  NOT_WORKFLOW_STATUSES,
  NOT_WORKFLOW_SUB_STATUSES,
  NOT_WORKFLOW_TEMPLATE_IDS,
  STATE_NAMES,
  YES_NO_OPTIONS,
} from "@/helpers/constants";
import { createOptionsUpdatedHandler } from "@/helpers/deals";
import { filterHasValue } from "@/helpers/filters";
import { useAuth } from "@/hooks/auth";
import { useDealsFilters } from "@/hooks/deals";
import { useIndustryOptions, useNumberOfEmployees } from "@/hooks/options";
import { useWorkflowCategoriesInfo } from "@/hooks/workflow";

import type {
  ActiveFilterWithExceptionMode,
  FilterState,
  IDealsFilters,
} from "@/models/applications";
import type { IClient } from "@/models/clients";
import type {
  IApplicationStatus,
  MultiSelectOption,
  UseOfFundsOption,
} from "@/models/options";
import type {
  DataOrchestrationMasterStatus,
  DataOrchestrationMasterStatusType,
} from "@/models/orchestration";

export const PRODUCT_TYPES = {
  "0": "NextGen",
  "1": "Legacy",
};

export const DEAL_MODES = {
  1: "Live",
  2: "Test",
};

export const useDealsFiltersState = (filters: IDealsFilters) => {
  const { getters } = useStore();
  const { t } = useI18n();
  const { industryTypeLabel, industryTypesOptions } = useIndustryOptions();
  const route = useRoute();
  const router = useRouter();

  const {
    fundersOptions,
    handleFundersOptionsUpdate,
    assigneeOptions,
    handleAssigneesOptionsUpdate,
    scorecardsOptions,
    handleScorecardsOptionsUpdate,
    scorecardGroupsOptions,
    handleScorecardGroupsOptionsUpdate,
    workflowStatuses,
    handleWorkflowStatusesOptionsUpdate,
    workflowSubStatuses,
    handleWorkflowSubStatusesOptionsUpdate,
    excludableFiltersMap,
    productTypes,
  } = useDealsFilters();

  const {
    numberOfEmployeesOptionsLabels,
    numberOfEmployeeIdByIndex,
    numberOfEmployeeIndexById,
  } = useNumberOfEmployees();

  const {
    isLendflowUser,
    isFundingAdvisor,
    isClientFundingAdvisor,
    isClientUnderwriter,
    isClientAdmin,
    isClient,
    isClientAnalyst,
    isAdmin,
    isAnalyst,
  } = useAuth();

  const { workflowCategoriesDropdownOptions } = useWorkflowCategoriesInfo();

  const partners = ref<Record<string, string>>({});
  const originPartners = ref<Record<string, string>>({});
  const submissionPartners = ref<Record<string, string>>({});
  const workflowTemplates = ref<Record<string, string>>({});
  const stageNamesOptions = ref<Record<string, string>>({});
  const productsRequestedOptions = ref<Record<string, string>>({});
  const lastEmailSentNames = ref<Record<string, string>>({});
  const fundingLenders = ref<Record<string, string>>({});
  const clientTrackingTokens = ref<string[]>([]);
  const additionalTrackingTokens = ref<string[]>([]);
  const utmParameters = ref<string[]>([]);
  const referrerHosts = ref<Record<string, string>>({});
  const brandNames = ref<Record<string, string>>({});

  const formatter = ref({
    date: "MM/DD/YYYY",
    month: "MMM",
  });
  const appliedWorkflows = ref<string[]>([]);

  const dealsLoading = computed<boolean>(() => getters["applications/loading"]);

  const revenueRanges = computed<Record<string, string>>(
    () => getters["options/revenueRanges"] || {}
  );

  const timeZones = computed<Record<string, string>>(
    () => getters["options/timeZones"]
  );

  const offerStatuses = computed<Record<string, string>>(
    () => getters["options/offerStatuses"]
  );

  const creditScores = computed<Record<string, string>>(
    () => getters["options/creditScores"]
  );

  const latestScoreCardStatuses = computed<Record<string, string>>(
    () => getters["options/latestScoreCardStatuses"]
  );

  const fundsNeededTimesOptions = computed<Record<string, string>>(
    () => getters["options/fundsNeededTimes"] || {}
  );

  const client = computed<IClient | null>(
    () => getters["auth/authClientSettings"]
  );

  const authClient = computed<IClient>(
    () => getters["auth/authClientSettings"]
  );

  const canUpdateDiscoveryStatus = computed(
    () => isLendflowUser || isClientAdmin || isClientUnderwriter
  );

  const canSeeAsLendflow = computed(() => {
    const clientCanCreateUnderwriter = !!client.value?.can_create_underwriter;
    return (
      (isClientAdmin ||
        isClientUnderwriter ||
        isClientAnalyst ||
        isClientFundingAdvisor) &&
      clientCanCreateUnderwriter
    );
  });

  const dataOrchestrationMasterStatuses = computed<
    Record<DataOrchestrationMasterStatusType, DataOrchestrationMasterStatus>
  >(() => getters["options/dataOrchestrationMasterStatuses"]);

  const numberOfEmployeesIndices = computed(() =>
    filters.number_of_employees.flatMap((index) =>
      String(numberOfEmployeeIndexById(Number(index)))
    )
  );

  const isLendflowTable = computed(
    () => isLendflowUser || canSeeAsLendflow.value
  );

  const documentsFilterLabel = computed(
    () =>
      `${t("STIPS.TITLE")}${filters.completed_documents.length ? "-" : ""}${
        filters.completed_documents == "1"
          ? t("DEALS.FILTERS.COMPLETE")
          : filters.completed_documents == "0"
            ? t("DEALS.FILTERS.INCOMPLETE")
            : ""
      }`
  );

  const applicationStatuses = computed(() => {
    const statusesValue: IApplicationStatus[] =
      getters["options/applicationStatuses"];

    if (cantSeeCompleteAndOfferTabs.value) {
      // Status 3 = Offer Out, 8 = Complete
      return statusesValue.filter(
        (status) => status.id === 3 || status.id === 8
      );
    }
    return statusesValue;
  });

  const applicationStatusesArray = computed(() =>
    applicationStatuses.value.map(({ status }) => status)
  );

  const selectedDealStatusIdsToIndexes = computed(() => {
    // Create a Map for O(1) lookup instead of O(n) findIndex for each status
    const statusIdToIndexMap = new Map<number, number>();
    applicationStatuses.value.forEach((status, index) => {
      statusIdToIndexMap.set(status.id, index);
    });

    return filters.statuses.map((statusId) => {
      const index = statusIdToIndexMap.get(Number(statusId));
      return (index ?? -1).toString();
    });
  });

  const stageCategories = computed<Record<string, string>>(
    () => getters["options/stageCategories"]
  );

  const workflowSegmentOptions = computed(() => {
    const types: MultiSelectOption[] = getters["options/workflowSegments"];
    return types.reduce<Record<string, string>>((acc, curr) => {
      acc[curr.id] = curr.name;
      return acc;
    }, {});
  });

  const automationOutcomes = computed<Record<string, string>>(
    () => getters["options/automationOutcomes"]
  );

  const cantSeeCompleteAndOfferTabs = computed(
    () =>
      (isClientUnderwriter || isClientAdmin || isClientAnalyst) &&
      !client.value?.can_create_underwriter
  );

  const canAssignAnalyst = computed(() => isAdmin || isAnalyst);

  const availableSources = computed<Record<string, string>>(
    () => getters["options/sources"]
  );

  const states = computed<Record<string, string>>(
    () => getters["options/states"]
  );

  const formatDealSource = (source: number) => {
    return (
      {
        1: t("DEALS.SOURCE.API"),
        2: t("DEALS.SOURCE.WIDGET"),
        3: t("DEALS.SOURCE.RENEWAL"),
        5: t("DEALS.SOURCE.DASHBOARD"),
        6: t("DEALS.SOURCE.PLACEMENT_LENDFLOW"),
        7: t("DEALS.SOURCE.PLACEMENT_EMAIL"),
        8: t("DEALS.SOURCE.PLACEMENT_MANUAL"),
        9: t("DEALS.SOURCE.PLACEMENT_API"),
      }[source] || "-"
    );
  };
  const formatDealType = (isLegacy: boolean) =>
    isLegacy ? "Legacy" : "NextGen";

  const handleClientTokensUpdate = (
    value: string[],
    type: "search" | "loadmore"
  ) => {
    if (type === "search") {
      clientTrackingTokens.value = value;
      return;
    }

    clientTrackingTokens.value = [...clientTrackingTokens.value, ...value];
  };

  const handleAdditionalTokensUpdate = (
    value: { token: string }[],
    type: "search" | "loadmore"
  ) => {
    if (type === "search") {
      additionalTrackingTokens.value = value.map((item) => item.token);
      return;
    }

    const newTokens = value.map((item) => item.token);
    additionalTrackingTokens.value = [
      ...additionalTrackingTokens.value,
      ...newTokens,
    ];
  };

  const handleUtmParamsUpdate = (
    value: string[],
    type: "search" | "loadmore"
  ) => {
    if (type === "search") {
      utmParameters.value = value;
      return;
    }

    const nextPageValues = value.reduce((res: Record<string, string>, item) => {
      res[item] = item;
      return res;
    }, {});

    utmParameters.value = { ...utmParameters.value, ...nextPageValues };
  };

  const handleLastEmailSentNameOptionsUpdate =
    createOptionsUpdatedHandler(lastEmailSentNames);

  const handleFundingLendersOptionsUpdate =
    createOptionsUpdatedHandler(fundingLenders);

  const handleReferrerHostsOptionsUpdate = createOptionsUpdatedHandler(
    referrerHosts,
    "host_name"
  );

  const handleBrandNamesOptionsUpdate = createOptionsUpdatedHandler(
    brandNames,
    "name"
  );

  const handlePartnersOptionsUpdate = createOptionsUpdatedHandler(partners);

  const handleOriginPartnersOptionsUpdate =
    createOptionsUpdatedHandler(originPartners);

  const handleSubmissionPartnersOptionsUpdate =
    createOptionsUpdatedHandler(submissionPartners);

  const handleStageNamesOptionsUpdate = createOptionsUpdatedHandler(
    stageNamesOptions,
    "stage_name"
  );

  const handleProductsRequested = createOptionsUpdatedHandler(
    productsRequestedOptions,
    "name"
  );

  const handleWorkflowTemplatesOptionsUpdate =
    createOptionsUpdatedHandler(workflowTemplates);

  const formatStateName = (state: string) => {
    return state in STATE_NAMES
      ? STATE_NAMES[state as keyof typeof STATE_NAMES]
          .toLowerCase()
          .split(" ")
          .map((word: string) =>
            word ? word[0].toUpperCase() + word.substring(1) : ""
          )
          .join(" ")
      : state || "";
  };

  const sortByChange = (value: string) => {
    if (filters.sort_by !== value) {
      filters.sort_by = value;
      return;
    }
    filters.sort_by = `-${value}`;
  };

  const formatUseOfFunds = (useOfFundsId: string) => {
    const getUseOfFunds: UseOfFundsOption = getters["options/useOfFunds"];
    return useOfFundsId ? getUseOfFunds(Number(useOfFundsId))?.toString() : "-";
  };

  const formatIndustryTypes = (industryId: string) =>
    industryTypeLabel(Number(industryId)) || "-";

  // Reactive filter state map — one entry per filterable column.
  // Using computed() inside reactive() gives Vue fine-grained per-field tracking.
  // Only the specific column's filter UI re-renders when its state changes.
  const filterStateMap: Record<string, FilterState> = {};

  // deal_mode
  filterStateMap["deal_mode"] = reactive({
    modelValue: computed(() => filters.deal_modes),
    numberOfFilters: computed(() => filters.deal_modes.length),
    plural: t("DEALS.TABLE_HEADERS.DEAL_MODES"),
  });

  // status
  filterStateMap["status"] = reactive({
    modelValue: computed(() => selectedDealStatusIdsToIndexes.value),
    numberOfFilters: computed(() => filters.statuses.length),
    options: computed(() => applicationStatusesArray.value),
    plural: t("DEALS.TABLE_HEADERS.DEAL_STAGES"),
  });

  // number_of_call_sessions (CallsFilter)
  filterStateMap["number_of_call_sessions"] = reactive({
    modelValue: computed(() => ({
      numberOfCallsRange: filters.number_of_calls,
      dateRange: filters.created_at_call,
    })),
    numberOfFilters: 0,
    hasFiltersSet: computed(
      () => !!filters.number_of_calls.length || !!filters.created_at_call.length
    ),
    plural: t("SMART_FILTERS.NUMBER_OF_CALLS", 0),
    sortBy: computed(() => filters.sort_by),
  });

  // data_orchestration_log.master_status
  filterStateMap["data_orchestration_log.master_status"] = reactive({
    modelValue: computed(() => filters.master_statuses),
    numberOfFilters: computed(() => filters.master_statuses.length),
    options: computed(() => dataOrchestrationMasterStatuses.value),
    plural: t("DEALS.TABLE_HEADERS.UNDERWRITING_STATUSES"),
  });

  // stage_category
  filterStateMap["stage_category"] = reactive({
    modelValue: computed(() => filters.stage_categories),
    numberOfFilters: computed(() => filters.stage_categories.length),
    options: computed(() => stageCategories.value),
    sortBy: computed(() => filters.sort_by),
    plural: t("DEALS.TABLE_HEADERS.STAGE_CATEGORY", 2),
  });

  // workflow_segment
  filterStateMap["workflow_segment"] = reactive({
    modelValue: computed(() => filters.workflow_segments),
    numberOfFilters: computed(() => filters.workflow_segments.length),
    options: computed(() => workflowSegmentOptions.value),
    plural: t("WORKFLOW.WORKFLOW_SEGMENT"),
  });

  // stage_name
  filterStateMap["stage_name"] = reactive({
    modelValue: computed(() => filters.stage_names),
    numberOfFilters: computed(() => filters.stage_names.length),
    options: computed(() => stageNamesOptions.value),
    sortBy: computed(() => filters.sort_by),
    plural: t("DEALS.TABLE_HEADERS.STAGE_NAME", 2),
  });

  filterStateMap["workflow_name"] = reactive({
    modelValue: computed(
      () => filters[excludableFiltersMap.value.workflowTemplates.activeMode]
    ),
    numberOfFilters: computed(
      () =>
        filters[excludableFiltersMap.value.workflowTemplates.activeMode].length
    ),
    options: computed(() => workflowTemplates.value),
    isExcludeModeEnabled: computed(
      () =>
        excludableFiltersMap.value.workflowTemplates.activeMode ===
        NOT_WORKFLOW_TEMPLATE_IDS
    ),
    sortBy: computed(() => filters.sort_by),
    plural: t("DEALS.TABLE_HEADERS.WORKFLOW_NAME", 2),
  });

  filterStateMap["client.name"] = reactive({
    modelValue: computed(
      () => filters[excludableFiltersMap.value.partners.activeMode]
    ),
    numberOfFilters: computed(
      () => filters[excludableFiltersMap.value.partners.activeMode].length
    ),
    options: computed(() => partners.value),
    isExcludeModeEnabled: computed(
      () => excludableFiltersMap.value.partners.activeMode === NOT_PARTNERS
    ),
    sortBy: computed(() => filters.sort_by),
    plural: t("DEALS.TABLE_HEADERS.ORGANIZATIONS"),
  });

  // created_at (date picker)
  filterStateMap["created_at"] = reactive({
    modelValue: computed(() => filters.created_at_range),
    numberOfFilters: 0,
    hasFiltersSet: computed(
      () => !!filters.created_at_range[0] && !!filters.created_at_range[1]
    ),
    sortBy: computed(() => filters.sort_by),
    plural: computed(
      () =>
        `${t("DEALS.TABLE_HEADERS.APP_STARTED")}: ${filters.created_at_range[0]} - ${filters.created_at_range[1]}`
    ),
    options: computed(() => ({
      formatter: formatter.value,
      disabled: dealsLoading.value,
    })),
  });

  // submission_partner.name
  filterStateMap["submission_partner.name"] = reactive({
    modelValue: computed(() => filters.submission_partners),
    numberOfFilters: computed(() => filters.submission_partners.length),
    options: computed(() => submissionPartners.value),
    sortBy: computed(() => filters.sort_by),
    plural: t("DEALS.TABLE_HEADERS.SUBMISSION_PARTNER"),
  });

  // finished_at (date picker)
  filterStateMap["finished_at"] = reactive({
    modelValue: computed(() => filters.finished_at_range),
    numberOfFilters: 0,
    hasFiltersSet: computed(
      () => !!filters.finished_at_range[0] && !!filters.finished_at_range[1]
    ),
    sortBy: computed(() => filters.sort_by),
    plural: computed(
      () =>
        `${t("DEALS.TABLE_HEADERS.APP_COMPLETED")}: ${filters.finished_at_range[0]} - ${filters.finished_at_range[1]}`
    ),
    options: computed(() => ({
      formatter: formatter.value,
      disabled: dealsLoading.value,
    })),
  });

  // accepted_offer.funded_date (date picker)
  filterStateMap["accepted_offer.funded_date"] = reactive({
    modelValue: computed(() => filters.funded_date_range),
    numberOfFilters: 0,
    hasFiltersSet: computed(
      () => !!filters.funded_date_range[0] && !!filters.funded_date_range[1]
    ),
    sortBy: computed(() => filters.sort_by),
    plural: computed(
      () =>
        `${t("DEALS.TABLE_HEADERS.FUNDED_DATE")}: ${filters.funded_date_range[0]} - ${filters.funded_date_range[1]}`
    ),
    options: computed(() => ({
      formatter: formatter.value,
      disabled: dealsLoading.value,
    })),
  });

  // funding_term_start_date (date picker)
  filterStateMap["funding_term_start_date"] = reactive({
    modelValue: computed(() => filters.funding_term_start_date_range),
    numberOfFilters: 0,
    hasFiltersSet: computed(
      () =>
        !!filters.funding_term_start_date_range[0] &&
        !!filters.funding_term_start_date_range[1]
    ),
    sortBy: computed(() => filters.sort_by),
    plural: computed(
      () =>
        `${t("DEALS.TABLE_HEADERS.FUNDING_TERM_START_DATE")}: ${filters.funding_term_start_date_range[0]} - ${filters.funding_term_start_date_range[1]}`
    ),
    options: computed(() => ({
      formatter: formatter.value,
      disabled: dealsLoading.value,
    })),
  });

  // funding_term_end_date (date picker)
  filterStateMap["funding_term_end_date"] = reactive({
    modelValue: computed(() => filters.funding_term_end_date_range),
    numberOfFilters: 0,
    hasFiltersSet: computed(
      () =>
        !!filters.funding_term_end_date_range[0] &&
        !!filters.funding_term_end_date_range[1]
    ),
    sortBy: computed(() => filters.sort_by),
    plural: computed(
      () =>
        `${t("DEALS.TABLE_HEADERS.FUNDING_TERM_END_DATE")}: ${filters.funding_term_end_date_range[0]} - ${filters.funding_term_end_date_range[1]}`
    ),
    options: computed(() => ({
      formatter: formatter.value,
      disabled: dealsLoading.value,
    })),
  });

  // last_call_range (date picker)
  filterStateMap["last_call_range"] = reactive({
    modelValue: computed(() => filters.last_call_range),
    numberOfFilters: 0,
    hasFiltersSet: computed(
      () => !!filters.last_call_range[0] && !!filters.last_call_range[1]
    ),
    sortBy: computed(() => filters.sort_by),
    plural: computed(
      () =>
        `${t("DEALS.TABLE_HEADERS.LAST_CALL")}: ${filters.last_call_range[0]} - ${filters.last_call_range[1]}`
    ),
    options: computed(() => ({
      formatter: formatter.value,
      disabled: dealsLoading.value,
    })),
  });

  // last_call_session.call_disposition
  filterStateMap["last_call_session.call_disposition"] = reactive({
    modelValue: computed(() => filters.last_call_disposition),
    numberOfFilters: computed(() => filters.last_call_disposition?.length ?? 0),
    options: computed(() => getters["options/callDispositionMap"]),
    sortBy: computed(() => filters.sort_by),
    plural: t("DEALS.TABLE_HEADERS.CALL_DISPOSITION"),
  });

  // source
  filterStateMap["source"] = reactive({
    modelValue: computed(() => filters.sources),
    numberOfFilters: computed(() => filters.sources.length),
    options: computed(() => availableSources.value),
    plural: t("DEALS.TABLE_HEADERS.SOURCES"),
  });

  // latest_offer_created_at (date picker)
  filterStateMap["latest_offer_created_at"] = reactive({
    modelValue: computed(() => filters.last_offer_created_range),
    numberOfFilters: 0,
    hasFiltersSet: computed(
      () =>
        !!filters.last_offer_created_range[0] &&
        !!filters.last_offer_created_range[1]
    ),
    sortBy: computed(() => filters.sort_by),
    plural: computed(
      () =>
        `${t("DEALS.TABLE_HEADERS.LATEST_OFFER_CREATED")}: ${filters.last_offer_created_range[0]} - ${filters.last_offer_created_range[1]}`
    ),
    options: computed(() => ({
      formatter: formatter.value,
      disabled: dealsLoading.value,
    })),
  });

  // workflow (workflow_categories)
  filterStateMap["workflow"] = reactive({
    modelValue: computed(() => filters.workflow_categories || []),
    numberOfFilters: computed(() => filters.workflow_categories.length),
    options: computed(() => workflowCategoriesDropdownOptions.value),
    plural: t("DEALS.TABLE_HEADERS.WORKFLOW_CATEGORIES"),
  });

  // is_business_credit (legacy workflow)
  filterStateMap["is_business_credit"] = reactive({
    modelValue: computed(() => appliedWorkflows.value),
    numberOfFilters: computed(() => appliedWorkflows.value.length),
    plural: `${t("COMMON.LEGACY")} ${t("DEALS.TABLE_HEADERS.WORKFLOW_CATEGORIES")}`,
  });

  // client_advisor_full_name
  filterStateMap["client_advisor_full_name"] = reactive({
    modelValue: computed(() => filters.client_funding_advisor_ids),
    numberOfFilters: computed(() => filters.client_funding_advisor_ids.length),
    plural: t("DEALS.TABLE_HEADERS.FUNDING_ADVISORS"),
  });

  // client_underwriter_full_name
  filterStateMap["client_underwriter_full_name"] = reactive({
    modelValue: computed(() => filters.client_underwriter_ids),
    numberOfFilters: computed(() => filters.client_underwriter_ids.length),
    plural: t("DEALS.TABLE_HEADERS.UNDERWRITERS"),
  });

  // client_analyst_full_name
  filterStateMap["client_analyst_full_name"] = reactive({
    modelValue: computed(() => filters.client_analyst_ids),
    numberOfFilters: computed(() => filters.client_analyst_ids.length),
    plural: t("DEALS.TABLE_HEADERS.ANALYSTS"),
  });

  // is_legacy
  filterStateMap["is_legacy"] = reactive({
    modelValue: computed(() => filters.is_legacy),
    numberOfFilters: 0,
    hasFiltersSet: computed(() =>
      Object.keys(PRODUCT_TYPES).includes(filters.is_legacy.toString())
    ),
    plural: computed(
      () => PRODUCT_TYPES[filters.is_legacy as keyof typeof PRODUCT_TYPES]
    ),
    options: PRODUCT_TYPES,
  });

  filterStateMap["advisor_full_name"] = reactive({
    modelValue: computed(
      () => filters[excludableFiltersMap.value.fundingAdvisorIds.activeMode]
    ),
    numberOfFilters: computed(
      () =>
        filters[excludableFiltersMap.value.fundingAdvisorIds.activeMode].length
    ),
    isExcludeModeEnabled: computed(
      () =>
        excludableFiltersMap.value.fundingAdvisorIds.activeMode ===
        NOT_FUNDING_ADVISOR_IDS
    ),
    plural: t("DEALS.TABLE_HEADERS.FUNDING_ADVISORS"),
  });

  filterStateMap["underwriter_full_name"] = reactive({
    modelValue: computed(
      () => filters[excludableFiltersMap.value.underwriterIds.activeMode]
    ),
    numberOfFilters: computed(
      () => filters[excludableFiltersMap.value.underwriterIds.activeMode].length
    ),
    isExcludeModeEnabled: computed(
      () =>
        excludableFiltersMap.value.underwriterIds.activeMode ===
        NOT_UNDERWRITER_IDS
    ),
    plural: t("DEALS.TABLE_HEADERS.UNDERWRITERS"),
  });

  filterStateMap["analyst_full_name"] = reactive({
    modelValue: computed(
      () => filters[excludableFiltersMap.value.analystIds.activeMode]
    ),
    numberOfFilters: computed(
      () => filters[excludableFiltersMap.value.analystIds.activeMode].length
    ),
    isExcludeModeEnabled: computed(
      () => excludableFiltersMap.value.analystIds.activeMode === NOT_ANALYST_IDS
    ),
    plural: t("DEALS.TABLE_HEADERS.LENDFLOW_ANALYST"),
  });

  filterStateMap["client_tracking_token"] = reactive({
    modelValue: computed(
      () => filters[excludableFiltersMap.value.clientTrackingTokens.activeMode]
    ),
    numberOfFilters: computed(
      () =>
        filters[excludableFiltersMap.value.clientTrackingTokens.activeMode]
          .length
    ),
    options: computed(() => clientTrackingTokens.value),
    isExcludeModeEnabled: computed(
      () =>
        excludableFiltersMap.value.clientTrackingTokens.activeMode ===
        NOT_CLIENT_TRACKING_TOKENS
    ),
    plural: t("DEALS.TABLE_HEADERS.CLIENT_UUID"),
  });

  filterStateMap["latest_tracking_token"] = reactive({
    modelValue: computed(
      () =>
        filters[excludableFiltersMap.value.additionalTrackingTokens.activeMode]
    ),
    numberOfFilters: computed(
      () =>
        filters[excludableFiltersMap.value.additionalTrackingTokens.activeMode]
          .length
    ),
    options: computed(() => additionalTrackingTokens.value),
    isExcludeModeEnabled: computed(
      () =>
        excludableFiltersMap.value.additionalTrackingTokens.activeMode ===
        NOT_ADDITIONAL_TRACKING_TOKENS
    ),
    sortBy: computed(() => filters.sort_by),
    plural: t("DEALS.TABLE_HEADERS.ADDITIONAL_TRACKING_TOKENS"),
  });

  // referrer_absolute_url
  filterStateMap["referrer_absolute_url"] = reactive({
    modelValue: computed(() => filters.referrer_host),
    numberOfFilters: computed(() => filters.referrer_host.length),
    options: computed(() => referrerHosts.value),
    plural: t("DEALS.TRACKING.REFERRER_URL"),
  });

  filterStateMap["brand_name"] = reactive({
    modelValue: computed(() => filters.brand_names),
    numberOfFilters: computed(() => filters.brand_names.length),
    options: computed(() => brandNames.value),
    plural: t("COMMON.BRAND", 2),
  });

  // utm_parameters
  filterStateMap["utm_parameters"] = reactive({
    modelValue: computed(() => filters.utm_parameters),
    numberOfFilters: computed(() => filters.utm_parameters.length),
    options: computed(() => utmParameters.value),
    plural: t("DEALS.TABLE_HEADERS.UTM_PARAMETERS"),
  });

  // business.industry_type_id
  filterStateMap["business.industry_type_id"] = reactive({
    modelValue: computed(() => filters.industry_types),
    numberOfFilters: computed(() => filters.industry_types?.length),
    options: computed(() => industryTypesOptions.value),
    plural: t("DEALS.TABLE_HEADERS.GENERAL_INDUSTRY"),
  });

  // business.address.state
  filterStateMap["business.address.state"] = reactive({
    modelValue: computed(() => filters.states),
    numberOfFilters: computed(() => filters.states.length),
    options: computed(() => states.value),
    plural: t("DEALS.TABLE_HEADERS.STATE"),
  });

  // funds_needed_time
  filterStateMap["funds_needed_time"] = reactive({
    modelValue: computed(() => filters.funds_needed_time || []),
    numberOfFilters: computed(() => filters.funds_needed_time?.length || 0),
    options: computed(() => fundsNeededTimesOptions.value),
    sortBy: computed(() => filters.sort_by),
    plural: t("DEALS.TABLE_HEADERS.WHEN_FUNDS_NEEDED"),
  });

  // amount_needed (MinAndMaxFilter)
  filterStateMap["amount_needed"] = reactive({
    modelValue: computed(() => filters.amount_needed_range),
    numberOfFilters: computed(() => filters.amount_needed_range?.length),
    sortBy: computed(() => filters.sort_by),
    plural: t("DEALS.TABLE_HEADERS.FUNDING_AMOUNT_REQUESTED"),
  });

  // business.actual_monthly_revenue (MinAndMaxFilter)
  filterStateMap["business.actual_monthly_revenue"] = reactive({
    modelValue: computed(() => filters.actual_monthly_revenue_range),
    numberOfFilters: computed(
      () => filters.actual_monthly_revenue_range?.length
    ),
    sortBy: computed(() => filters.sort_by),
    plural: t("DEALS.TABLE_HEADERS.ACTUAL_MONTHLY_REVENUE"),
  });

  filterStateMap["workflow_status.status"] = reactive({
    modelValue: computed(
      () => filters[excludableFiltersMap.value.workflowStatuses.activeMode]
    ),
    numberOfFilters: computed(
      () =>
        filters[excludableFiltersMap.value.workflowStatuses.activeMode].length
    ),
    options: computed(() => workflowStatuses.value),
    isExcludeModeEnabled: computed(
      () =>
        excludableFiltersMap.value.workflowStatuses.activeMode ===
        NOT_WORKFLOW_STATUSES
    ),
    sortBy: computed(() => filters.sort_by),
    plural: `${t("COMMON.WORKFLOW")} ${t("COMMON.STATUS", 2)}`,
  });

  filterStateMap["workflow_status.sub_status"] = reactive({
    modelValue: computed(
      () => filters[excludableFiltersMap.value.workflowSubStatuses.activeMode]
    ),
    numberOfFilters: computed(
      () =>
        filters[excludableFiltersMap.value.workflowSubStatuses.activeMode]
          .length
    ),
    options: computed(() => workflowSubStatuses.value),
    isExcludeModeEnabled: computed(
      () =>
        excludableFiltersMap.value.workflowSubStatuses.activeMode ===
        NOT_WORKFLOW_SUB_STATUSES
    ),
    sortBy: computed(() => filters.sort_by),
    plural: `${t("COMMON.WORKFLOW")} ${t("COMMON.SUB_STATUS", 2)}`,
  });

  // funded_amount (MinAndMaxFilter)
  filterStateMap["funded_amount"] = reactive({
    modelValue: computed(() => filters.funded_amount_range),
    numberOfFilters: computed(() => filters.funded_amount_range?.length),
    sortBy: computed(() => filters.sort_by),
    plural: t("DEALS.TABLE_HEADERS.FUNDED_AMOUNT"),
  });

  filterStateMap["origin_client.name"] = reactive({
    modelValue: computed(
      () => filters[excludableFiltersMap.value.originPartners.activeMode]
    ),
    numberOfFilters: computed(
      () =>
        filters[excludableFiltersMap.value.originPartners.activeMode]?.length ||
        0
    ),
    options: computed(() => originPartners.value),
    isExcludeModeEnabled: computed(
      () =>
        excludableFiltersMap.value.originPartners.activeMode ===
        NOT_ORIGIN_PARTNERS
    ),
    sortBy: computed(() => filters.sort_by),
    plural: t("DEALS.TABLE_HEADERS.ORIGIN_PARTNER"),
  });

  // stated_monthly_revenue
  filterStateMap["stated_monthly_revenue"] = reactive({
    modelValue: computed(() => filters.stated_monthly_revenue),
    numberOfFilters: 0,
    hasFiltersSet: computed(() => !!filters.stated_monthly_revenue.length),
    options: computed(() => revenueRanges.value),
    plural: t("DEALS.TABLE_HEADERS.STATED_MONTHLY_REVENUE"),
  });

  // is_signed
  filterStateMap["is_signed"] = reactive({
    modelValue: computed(() => filters.is_signed),
    numberOfFilters: 0,
    hasFiltersSet: computed(() => filterHasValue(filters.is_signed)),
    plural: computed(
      () =>
        `${t("DEALS.TABLE_HEADERS.SIGNED_APPLICATION")}-${filterHasValue(filters.is_signed) ? YES_NO_OPTIONS[filters.is_signed as keyof typeof YES_NO_OPTIONS] : ""}`
    ),
  });

  // plaid_connection
  filterStateMap["plaid_connection"] = reactive({
    modelValue: computed(() => filters.has_plaid_connection),
    numberOfFilters: 0,
    hasFiltersSet: computed(() => filterHasValue(filters.has_plaid_connection)),
    plural: computed(
      () =>
        `${t("DEALS.TABLE_HEADERS.PLAID_CONNECTION")}-${filterHasValue(filters.has_plaid_connection) ? YES_NO_OPTIONS[filters.has_plaid_connection as keyof typeof YES_NO_OPTIONS] : ""}`
    ),
  });

  // is_discovery_complete
  filterStateMap["is_discovery_complete"] = reactive({
    modelValue: computed(() => filters.is_discovery_complete),
    numberOfFilters: 0,
    hasFiltersSet: computed(() =>
      filterHasValue(filters.is_discovery_complete)
    ),
    plural: computed(
      () =>
        `${t("DEALS.TABLE_HEADERS.DISCOVERY")}-${filterHasValue(filters.is_discovery_complete) ? DISCOVERY_FILTER_OPTIONS[filters.is_discovery_complete as keyof typeof DISCOVERY_FILTER_OPTIONS] : ""}`
    ),
  });

  // time_in_current_stage (MinAndMaxFilter)
  filterStateMap["time_in_current_stage"] = reactive({
    modelValue: computed(() => filters.time_in_current_stage_range),
    numberOfFilters: computed(
      () => filters.time_in_current_stage_range?.length
    ),
    sortBy: computed(() => filters.sort_by),
    plural: t("DEALS.TABLE_HEADERS.TIME_IN_CURRENT_STAGE"),
  });

  // score_card_name
  filterStateMap["score_card_name"] = reactive({
    modelValue: computed(() => filters.score_card_ids),
    numberOfFilters: computed(() => filters.score_card_ids?.length),
    options: computed(() => scorecardsOptions.value),
    sortBy: computed(() => filters.sort_by),
    plural: `${t("COMMON.PRIMARY")} ${t("SCORECARDS.NAME")}`,
  });

  // score_card_group_name
  filterStateMap["score_card_group_name"] = reactive({
    modelValue: computed(() => filters.score_card_group_ids),
    numberOfFilters: computed(() => filters.score_card_group_ids?.length),
    options: computed(() => scorecardGroupsOptions.value),
    sortBy: computed(() => filters.sort_by),
    plural: `${t("COMMON.PRIMARY")} ${t("SCORECARDS.SCORECARD_GROUP_NAME")}`,
  });

  // score_card_result.points (MinAndMaxFilter)
  filterStateMap["score_card_result.points"] = reactive({
    modelValue: computed(() => filters.score_card_points_range),
    numberOfFilters: computed(() => filters.score_card_points_range?.length),
    sortBy: computed(() => filters.sort_by),
    plural: `${t("COMMON.PRIMARY")} ${t("SCORECARDS.SCORECARD")} ${t("COMMON.SCORE")}`,
  });

  // score_card_group_result.points (MinAndMaxFilter)
  filterStateMap["score_card_group_result.points"] = reactive({
    modelValue: computed(() => filters.score_card_group_points_range),
    numberOfFilters: computed(
      () => filters.score_card_group_points_range?.length
    ),
    sortBy: computed(() => filters.sort_by),
    plural: `${t("COMMON.PRIMARY")} ${t("SCORECARDS.SCORECARD_GROUP")} ${t("COMMON.SCORE")}`,
  });

  // time_to_first_offer (MinAndMaxFilter)
  filterStateMap["time_to_first_offer"] = reactive({
    modelValue: computed(() => filters.time_to_first_offer_range),
    numberOfFilters: computed(() => filters.time_to_first_offer_range?.length),
    sortBy: computed(() => filters.sort_by),
    plural: t("DEALS.TABLE_HEADERS.TIME_TO_OFFER_CREATION"),
  });

  // placements_count (MinAndMaxFilter)
  filterStateMap["placements_count"] = reactive({
    modelValue: computed(() => filters.placements_count_range),
    numberOfFilters: 0,
    hasFiltersSet: computed(() => !!filters.placements_count_range?.length),
    sortBy: computed(() => filters.sort_by),
    plural: t("DEALS.TABLE_HEADERS.NUMBER_OF_PLACEMENTS"),
  });

  // has_placement
  filterStateMap["has_placement"] = reactive({
    modelValue: computed(() => filters.has_placement),
    numberOfFilters: 0,
    hasFiltersSet: computed(() => filterHasValue(filters.has_placement)),
    sortBy: computed(() => filters.sort_by),
    plural: computed(
      () =>
        `${t("DEALS.TABLE_HEADERS.HAS_PLACEMENTS")}-${filterHasValue(filters.has_placement) ? YES_NO_OPTIONS[filters.has_placement as keyof typeof YES_NO_OPTIONS] : ""}`
    ),
  });

  // offer_count (MinAndMaxFilter)
  filterStateMap["offer_count"] = reactive({
    modelValue: computed(() => filters.offers_count_range),
    numberOfFilters: 0,
    hasFiltersSet: computed(() => !!filters.offers_count_range?.length),
    sortBy: computed(() => filters.sort_by),
    plural: t("DEALS.TABLE_HEADERS.NUMBER_OF_OFFERS"),
  });

  // has_offer
  filterStateMap["has_offer"] = reactive({
    modelValue: computed(() => filters.has_offer),
    numberOfFilters: 0,
    hasFiltersSet: computed(() => filterHasValue(filters.has_offer)),
    sortBy: computed(() => filters.sort_by),
    plural: computed(
      () =>
        `${t("DEALS.TABLE_HEADERS.HAS_OFFERS")}-${filterHasValue(filters.has_offer) ? YES_NO_OPTIONS[filters.has_offer as keyof typeof YES_NO_OPTIONS] : ""}`
    ),
  });

  // bank_statements_count
  filterStateMap["bank_statements_count"] = reactive({
    modelValue: computed(() => filters.has_bank_statements),
    numberOfFilters: 0,
    hasFiltersSet: computed(() => filterHasValue(filters.has_bank_statements)),
    plural: computed(
      () =>
        `${t("DEALS.TABLE_HEADERS.BANK_STATEMENTS")}-${filterHasValue(filters.has_bank_statements) ? YES_NO_OPTIONS[filters.has_bank_statements as keyof typeof YES_NO_OPTIONS] : ""}`
    ),
  });

  // has_bank_data
  filterStateMap["has_bank_data"] = reactive({
    modelValue: computed(() => filters.has_bank_data),
    numberOfFilters: 0,
    hasFiltersSet: computed(() => filterHasValue(filters.has_bank_data)),
    plural: computed(
      () =>
        `${t("DEALS.TABLE_HEADERS.BANK_DATA")}-${filterHasValue(filters.has_bank_data) ? YES_NO_OPTIONS[filters.has_bank_data as keyof typeof YES_NO_OPTIONS] : ""}`
    ),
  });

  // stage_management
  filterStateMap["stage_management"] = reactive({
    modelValue: computed(() => filters.stage_management),
    numberOfFilters: computed(() => filters.stage_management.length),
    plural: `${t("DEALS.TABLE_HEADERS.STAGE_CONTROL")}`,
  });

  // business.number_of_employees_id
  filterStateMap["business.number_of_employees_id"] = reactive({
    modelValue: computed(() => numberOfEmployeesIndices.value),
    numberOfFilters: computed(() => filters.number_of_employees.length),
    options: computed(() => numberOfEmployeesOptionsLabels.value),
    plural: t("DEALS.TABLE_HEADERS.NUMBER_OF_EMPLOYEES"),
  });

  filterStateMap["funded_offer_funders"] = reactive({
    modelValue: computed(
      () => filters[excludableFiltersMap.value.fundedOfferFunders.activeMode]
    ),
    numberOfFilters: computed(
      () =>
        filters[excludableFiltersMap.value.fundedOfferFunders.activeMode].length
    ),
    options: computed(() => fundingLenders.value),
    isExcludeModeEnabled: computed(
      () =>
        excludableFiltersMap.value.fundedOfferFunders.activeMode ===
        NOT_FUNDED_OFFER_FUNDERS
    ),
    sortBy: computed(() => filters.sort_by),
    plural: t("DEALS.TABLE_HEADERS.FUNDING_LENDERS"),
  });

  filterStateMap["funders_with_offers"] = reactive({
    modelValue: computed(
      () => filters[excludableFiltersMap.value.fundersWithOffers.activeMode]
    ),
    numberOfFilters: computed(
      () =>
        filters[excludableFiltersMap.value.fundersWithOffers.activeMode].length
    ),
    options: computed(() => fundersOptions.value),
    isExcludeModeEnabled: computed(
      () =>
        excludableFiltersMap.value.fundersWithOffers.activeMode ===
        NOT_FUNDERS_WITH_OFFERS
    ),
    plural: t("DEALS.TABLE_HEADERS.FUNDERS_WITH_OFFERS"),
  });

  filterStateMap["funders_with_placements"] = reactive({
    modelValue: computed(
      () => filters[excludableFiltersMap.value.fundersWithPlacements.activeMode]
    ),
    numberOfFilters: computed(
      () =>
        filters[excludableFiltersMap.value.fundersWithPlacements.activeMode]
          .length
    ),
    options: computed(() => fundersOptions.value),
    isExcludeModeEnabled: computed(
      () =>
        excludableFiltersMap.value.fundersWithPlacements.activeMode ===
        NOT_FUNDERS_WITH_PLACEMENTS
    ),
    plural: t("DEALS.TABLE_HEADERS.FUNDERS_WITH_PLACEMENTS"),
  });

  // first_placement_datetime (date picker)
  filterStateMap["first_placement_datetime"] = reactive({
    modelValue: computed(() => filters.first_placement_range),
    numberOfFilters: 0,
    hasFiltersSet: computed(
      () =>
        !!filters.first_placement_range[0] && !!filters.first_placement_range[1]
    ),
    sortBy: computed(() => filters.sort_by),
    plural: computed(
      () =>
        `${t("DEALS.TABLE_HEADERS.FIRST_PLACEMENT_TIME")}: ${filters.first_placement_range[0]} - ${filters.first_placement_range[1]}`
    ),
    options: computed(() => ({
      formatter: formatter.value,
      disabled: dealsLoading.value,
    })),
  });

  // last_placement_datetime (date picker)
  filterStateMap["last_placement_datetime"] = reactive({
    modelValue: computed(() => filters.last_placement_range),
    numberOfFilters: 0,
    hasFiltersSet: computed(
      () =>
        !!filters.last_placement_range[0] && !!filters.last_placement_range[1]
    ),
    sortBy: computed(() => filters.sort_by),
    plural: computed(
      () =>
        `${t("DEALS.TABLE_HEADERS.LAST_PLACEMENT_TIME")}: ${filters.last_placement_range[0]} - ${filters.last_placement_range[1]}`
    ),
    options: computed(() => ({
      formatter: formatter.value,
      disabled: dealsLoading.value,
    })),
  });

  // highest_offer_amount (MinAndMaxFilter)
  filterStateMap["highest_offer_amount"] = reactive({
    modelValue: computed(() => filters.highest_offer_amount_range),
    numberOfFilters: computed(() => filters.highest_offer_amount_range?.length),
    sortBy: computed(() => filters.sort_by),
    plural: t("DEALS.TABLE_HEADERS.HIGHEST_OFFER_AMOUNT"),
  });

  // time_in_business (MinAndMaxFilter)
  filterStateMap["time_in_business"] = reactive({
    modelValue: computed(() => filters.time_in_business_range),
    numberOfFilters: computed(() => filters.time_in_business_range?.length),
    sortBy: computed(() => filters.sort_by),
    plural: t("DEALS.TABLE_HEADERS.TIME_IN_BUSINESS"),
  });

  // business.address.time_zone
  filterStateMap["business.address.time_zone"] = reactive({
    modelValue: computed(() => filters.business_time_zones),
    numberOfFilters: computed(() => filters.business_time_zones.length),
    options: computed(() => timeZones.value),
    sortBy: computed(() => filters.sort_by),
    plural: t("DEALS.LEXIS_NEXIS.UCC.TIME_ZONE"),
  });

  // personal_information.credit_score
  filterStateMap["personal_information.credit_score"] = reactive({
    modelValue: computed(() => filters.primary_owner_stated_credit_scores),
    numberOfFilters: computed(
      () => filters.primary_owner_stated_credit_scores.length
    ),
    options: computed(() => creditScores.value),
    sortBy: computed(() => filters.sort_by),
    plural: t("CREDIT.STATED_SCORE"),
  });

  // score_card_result.status
  filterStateMap["score_card_result.status"] = reactive({
    modelValue: computed(() => filters.latest_score_card_statuses),
    numberOfFilters: computed(() => filters.latest_score_card_statuses.length),
    options: computed(() => latestScoreCardStatuses.value),
    sortBy: computed(() => filters.sort_by),
    plural: t("DEALS.TABLE_HEADERS.SCORECARD_RESULT_STATUS"),
  });

  // personal_information.actual_credit_score (MinAndMaxFilter)
  filterStateMap["personal_information.actual_credit_score"] = reactive({
    modelValue: computed(() => filters.primary_owner_actual_credit_score_range),
    numberOfFilters: computed(
      () => filters.primary_owner_actual_credit_score_range?.length
    ),
    sortBy: computed(() => filters.sort_by),
    plural: t("CREDIT.ACTUAL_SCORE"),
  });

  // priority_offer_status
  filterStateMap["priority_offer_status"] = reactive({
    modelValue: computed(() => filters.priority_offer_statuses),
    numberOfFilters: computed(() => filters.priority_offer_statuses.length),
    options: computed(() => offerStatuses.value),
    sortBy: computed(() => filters.sort_by),
    plural: `${t("ACTIVITY.FILTERS.PRIORITY")} ${t("DEALS.TRACKING.OFFER_STATUS")}`,
  });

  // last_updated_offer_status
  filterStateMap["last_updated_offer_status"] = reactive({
    modelValue: computed(() => filters.last_updated_offer_statuses),
    numberOfFilters: computed(() => filters.last_updated_offer_statuses.length),
    options: computed(() => offerStatuses.value),
    sortBy: computed(() => filters.sort_by),
    plural: `${t("LOGS.LAST_UPDATED")} ${t("DEALS.TRACKING.OFFER_STATUS")}`,
  });

  // latest_workflow_orchestration_log_status
  filterStateMap["latest_workflow_orchestration_log_status"] = reactive({
    modelValue: computed(
      () => filters.latest_workflow_orchestration_log_statuses
    ),
    numberOfFilters: computed(
      () => filters.latest_workflow_orchestration_log_statuses.length
    ),
    options: computed(() => automationOutcomes.value),
    sortBy: computed(() => filters.sort_by),
    plural: t("DEALS.TABLE_HEADERS.AUTOMATION"),
  });

  // latest_email_validation_message
  filterStateMap["latest_email_validation_message"] = reactive({
    modelValue: computed(() => filters.has_latest_email_validation_error),
    numberOfFilters: 0,
    hasFiltersSet: computed(() =>
      filterHasValue(filters.has_latest_email_validation_error)
    ),
    plural: t("DEALS.TABLE_HEADERS.EMAIL_VALIDATION_MESSAGE"),
  });

  // number_of_emails_sent (MinAndMaxFilter)
  filterStateMap["number_of_emails_sent"] = reactive({
    modelValue: computed(() => filters.number_of_emails_sent_range),
    numberOfFilters: computed(
      () => filters.number_of_emails_sent_range?.length
    ),
    sortBy: computed(() => filters.sort_by),
    plural: t("DEALS.TABLE_HEADERS.NUMBER_OF_EMAILS_SENT", 2),
  });

  // number_of_emails_received (MinAndMaxFilter)
  filterStateMap["number_of_emails_received"] = reactive({
    modelValue: computed(() => filters.number_of_emails_received_range),
    numberOfFilters: computed(
      () => filters.number_of_emails_received_range?.length
    ),
    sortBy: computed(() => filters.sort_by),
    plural: t("DEALS.TABLE_HEADERS.NUMBER_OF_EMAILS_RECEIVED", 2),
  });

  // latest_email_sent.timestamp (date picker)
  filterStateMap["latest_email_sent.timestamp"] = reactive({
    modelValue: computed(() => filters.last_email_sent_date_range),
    numberOfFilters: 0,
    hasFiltersSet: computed(
      () =>
        !!filters.last_email_sent_date_range[0] &&
        !!filters.last_email_sent_date_range[1]
    ),
    sortBy: computed(() => filters.sort_by),
    plural: computed(
      () =>
        `${t("DEALS.TABLE_HEADERS.LAST_EMAIL_SENT")}: ${filters.last_email_sent_date_range[0]} - ${filters.last_email_sent_date_range[1]}`
    ),
    options: computed(() => ({
      formatter: formatter.value,
      disabled: dealsLoading.value,
    })),
  });

  // latest_email_sent.template_name
  filterStateMap["latest_email_sent.template_name"] = reactive({
    modelValue: computed(() => filters.last_email_sent_name),
    numberOfFilters: computed(() => filters.last_email_sent_name.length),
    options: computed(() => lastEmailSentNames.value),
    sortBy: computed(() => filters.sort_by),
    plural: t("DEALS.TABLE_HEADERS.LAST_EMAIL_SENT_NAME", 2),
  });

  // assigned_onboarding_call_sessions (CallsFilter)
  filterStateMap["assigned_onboarding_call_sessions"] = reactive({
    modelValue: computed(() => ({
      numberOfCallsRange: filters.assigned_onboarding_calls,
      dateRange: filters.created_at_assigned_onboarding_call,
    })),
    numberOfFilters: 0,
    hasFiltersSet: computed(
      () =>
        !!filters.assigned_onboarding_calls.length ||
        !!filters.created_at_assigned_onboarding_call.length
    ),
    sortBy: computed(() => filters.sort_by),
    plural: t("SMART_FILTERS.ASSIGNED_ONBOARDING_CALLS"),
  });

  // total_onboarding_call_sessions (CallsFilter)
  filterStateMap["total_onboarding_call_sessions"] = reactive({
    modelValue: computed(() => ({
      numberOfCallsRange: filters.total_onboarding_calls,
      dateRange: filters.created_at_onboarding_call,
    })),
    numberOfFilters: 0,
    hasFiltersSet: computed(
      () =>
        !!filters.total_onboarding_calls.length ||
        !!filters.created_at_onboarding_call.length
    ),
    sortBy: computed(() => filters.sort_by),
    plural: t("SMART_FILTERS.TOTAL_ONBOARDING_CALLS"),
  });

  // closest_due_task (task_is_created)
  filterStateMap["closest_due_task"] = reactive({
    modelValue: computed(() => filters.task_is_created),
    numberOfFilters: 0,
    hasFiltersSet: computed(() => filterHasValue(filters.task_is_created)),
    plural: computed(
      () =>
        `${t("DEALS.TABLE_HEADERS.TASK_CREATED")}-${filterHasValue(filters.task_is_created) ? YES_NO_OPTIONS[filters.task_is_created as keyof typeof YES_NO_OPTIONS] : ""}`
    ),
  });

  // repayment_percentage (RangeFilter)
  filterStateMap["repayment_percentage"] = reactive({
    modelValue: computed(() => filters.repayment_percentage_range),
    numberOfFilters: 0,
    hasFiltersSet: computed(
      () =>
        !!filters.repayment_percentage_range[0] &&
        !!filters.repayment_percentage_range[1]
    ),
    sortBy: computed(() => filters.sort_by),
    plural: computed(
      () =>
        `${t("DEALS.TABLE_HEADERS.REPAYMENT_PERCENTAGE_RANGE")}: ${filters.repayment_percentage_range[0]} - ${filters.repayment_percentage_range[1]}`
    ),
  });

  filterStateMap["closest_due_task.assignee"] = reactive({
    modelValue: computed(
      () => filters[excludableFiltersMap.value.taskAssignees.activeMode]
    ),
    numberOfFilters: computed(
      () => filters[excludableFiltersMap.value.taskAssignees.activeMode].length
    ),
    options: computed(() => assigneeOptions.value),
    isExcludeModeEnabled: computed(
      () =>
        excludableFiltersMap.value.taskAssignees.activeMode ===
        NOT_TASK_ASSIGNEES
    ),
    plural: t("DEALS.TABLE_HEADERS.TASK_ASSIGNEE", 2),
  });

  // last_call_session.user
  filterStateMap["last_call_session.user"] = reactive({
    modelValue: computed(() => filters.last_call_by),
    numberOfFilters: computed(() => filters.last_call_by.length),
    options: computed(() => assigneeOptions.value),
    plural: t("DEALS.TABLE_HEADERS.LAST_CALL_BY"),
  });

  // closest_due_task.due_at (date picker)
  filterStateMap["closest_due_task.due_at"] = reactive({
    modelValue: computed(() => filters.task_due_date_range),
    numberOfFilters: 0,
    hasFiltersSet: computed(
      () => !!filters.task_due_date_range[0] && !!filters.task_due_date_range[1]
    ),
    sortBy: computed(() => filters.sort_by),
    plural: computed(
      () =>
        `${t("DEALS.TABLE_HEADERS.TASK_DUE_DATE")}: ${filters.task_due_date_range[0]} - ${filters.task_due_date_range[1]}`
    ),
    options: computed(() => ({
      formatter: formatter.value,
      disabled: dealsLoading.value,
    })),
  });

  // closest_due_task.created_at (date picker)
  filterStateMap["closest_due_task.created_at"] = reactive({
    modelValue: computed(() => filters.task_created_date_range),
    numberOfFilters: 0,
    hasFiltersSet: computed(
      () =>
        !!filters.task_created_date_range[0] &&
        !!filters.task_created_date_range[1]
    ),
    sortBy: computed(() => filters.sort_by),
    plural: computed(
      () =>
        `${t("DEALS.TABLE_HEADERS.TASK_CREATED_DATE")}: ${filters.task_created_date_range[0]} - ${filters.task_created_date_range[1]}`
    ),
    options: computed(() => ({
      formatter: formatter.value,
      disabled: dealsLoading.value,
    })),
  });

  filterStateMap["closest_due_task.priority_level"] = reactive({
    modelValue: computed(
      () => filters[excludableFiltersMap.value.taskPriorityLevels.activeMode]
    ),
    numberOfFilters: computed(
      () =>
        filters[excludableFiltersMap.value.taskPriorityLevels.activeMode].length
    ),
    isExcludeModeEnabled: computed(
      () =>
        excludableFiltersMap.value.taskPriorityLevels.activeMode ===
        NOT_TASK_PRIORITY_LEVELS
    ),
    plural: t("DEALS.TABLE_HEADERS.TASK_PRIORITY", 2),
  });

  // task_statuses (column key is "closest_due_task" — same as task_is_created, lookup by filter name)
  filterStateMap["task_statuses"] = reactive({
    modelValue: computed(
      () => filters[excludableFiltersMap.value.taskStatuses.activeMode]
    ),
    numberOfFilters: computed(
      () => filters[excludableFiltersMap.value.taskStatuses.activeMode].length
    ),
    isExcludeModeEnabled: computed(
      () =>
        excludableFiltersMap.value.taskStatuses.activeMode === NOT_TASK_STATUSES
    ),
    plural: t("DEALS.TABLE_HEADERS.TASK_STATUS", 2),
  });

  // products_requested
  filterStateMap["products_requested"] = reactive({
    modelValue: computed(() => filters.products_requested),
    numberOfFilters: computed(() => filters.products_requested?.length),
    options: computed(() => productsRequestedOptions.value),
    plural: t("DEALS.DEAL_PROGRESS.BUSINESS_INFO.PRODUCTS_REQUESTED"),
  });

  // business.naics_code
  filterStateMap["business.naics_code"] = reactive({
    modelValue: computed(() => filters.naics_codes),
    numberOfFilters: computed(() => filters.naics_codes.length),
    plural: t("DEALS.TABLE_HEADERS.NAICS_CODES"),
  });

  // required_documents
  filterStateMap["required_documents"] = reactive({
    modelValue: computed(() => filters.required_documents),
    numberOfFilters: computed(() => filters.required_documents?.length),
    hasFiltersSet: computed(
      () =>
        !!filters.required_documents?.length ||
        !!filters.completed_documents.length
    ),
    plural: computed(() => documentsFilterLabel.value),
    options: computed(() => ({
      documentsCompleted: filters.completed_documents,
    })),
  });

  filterStateMap["product_type_funded"] = reactive({
    modelValue: computed(
      () => filters[excludableFiltersMap.value.productTypeFunded.activeMode]
    ),
    numberOfFilters: computed(
      () =>
        filters[excludableFiltersMap.value.productTypeFunded.activeMode]
          ?.length ?? 0
    ),
    options: computed(() => productTypes.value),
    isExcludeModeEnabled: computed(
      () =>
        excludableFiltersMap.value.productTypeFunded.activeMode ===
        NOT_PRODUCT_TYPE_FUNDED
    ),
    plural: t("DEALS.TABLE_HEADERS.PRODUCT_TYPE_FUNDED"),
  });

  const handleQueryChange = (key: keyof ActiveFilterWithExceptionMode) => {
    const { activeMode, negative, positive } = excludableFiltersMap.value[key];

    const query = JSON.parse(JSON.stringify(route.query));

    // get query value for previous mode
    const queryValue =
      activeMode === negative ? query[`${positive}[]`] : query[`${negative}[]`];

    if (queryValue === undefined) {
      return;
    }

    const oldMode = activeMode === negative ? positive : negative;
    delete query[`${oldMode}[]`];

    const queryArray = convertToArray(queryValue) as string[] & number[];
    query[activeMode] = queryArray;

    filters[activeMode] = queryArray;
    filters[oldMode] = [];

    router.push({ query });
  };

  watch(
    () => route.query,
    (updatedQuery) => {
      getObjectKeys(excludableFiltersMap.value).forEach((key) => {
        const filter = excludableFiltersMap.value[key];
        filter.activeMode = updatedQuery[`${filter.negative}[]`]
          ? filter.negative
          : filter.positive;
      });
    }
  );

  return {
    filterStateMap,
    sortByChange,
    handleQueryChange,
    // Option refs
    partners,
    originPartners,
    submissionPartners,
    workflowTemplates,
    stageNamesOptions,
    productsRequestedOptions,
    lastEmailSentNames,
    fundingLenders,
    clientTrackingTokens,
    additionalTrackingTokens,
    utmParameters,
    referrerHosts,
    brandNames,
    // Refs/computed
    formatter,
    appliedWorkflows,
    dealsLoading,
    revenueRanges,
    timeZones,
    offerStatuses,
    creditScores,
    latestScoreCardStatuses,
    fundsNeededTimesOptions,
    client,
    authClient,
    canUpdateDiscoveryStatus,
    canSeeAsLendflow,
    dataOrchestrationMasterStatuses,
    numberOfEmployeesIndices,
    isLendflowTable,
    documentsFilterLabel,
    applicationStatuses,
    applicationStatusesArray,
    selectedDealStatusIdsToIndexes,
    stageCategories,
    workflowSegmentOptions,
    automationOutcomes,
    cantSeeCompleteAndOfferTabs,
    canAssignAnalyst,
    availableSources,
    states,
    // Handler functions
    handleClientTokensUpdate,
    handleAdditionalTokensUpdate,
    handleUtmParamsUpdate,
    handleLastEmailSentNameOptionsUpdate,
    handleFundingLendersOptionsUpdate,
    handleReferrerHostsOptionsUpdate,
    handleBrandNamesOptionsUpdate,
    handlePartnersOptionsUpdate,
    handleOriginPartnersOptionsUpdate,
    handleSubmissionPartnersOptionsUpdate,
    handleStageNamesOptionsUpdate,
    handleProductsRequested,
    handleWorkflowTemplatesOptionsUpdate,
    formatStateName,
    formatDealSource,
    formatDealType,
    formatUseOfFunds,
    formatIndustryTypes,
    // Hook returns needed by columns
    excludableFiltersMap,
    fundersOptions,
    handleFundersOptionsUpdate,
    assigneeOptions,
    handleAssigneesOptionsUpdate,
    scorecardsOptions,
    handleScorecardsOptionsUpdate,
    scorecardGroupsOptions,
    handleScorecardGroupsOptionsUpdate,
    workflowStatuses,
    handleWorkflowStatusesOptionsUpdate,
    workflowSubStatuses,
    handleWorkflowSubStatusesOptionsUpdate,
    productTypes,
    industryTypeLabel,
    industryTypesOptions,
    numberOfEmployeesOptionsLabels,
    numberOfEmployeeIdByIndex,
    numberOfEmployeeIndexById,
    workflowCategoriesDropdownOptions,
    // Auth booleans
    isLendflowUser,
    isFundingAdvisor,
    isClientFundingAdvisor,
    isClientUnderwriter,
    isClientAdmin,
    isClient,
    isClientAnalyst,
    isAdmin,
    isAnalyst,
  };
};
